import { useState, useEffect } from 'react';

export default function ContractsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    customer_name: '',
    contract_date: '',
    total_contract_value: '',
    currency: 'USD',
    start_date: '',
    end_date: '',
    description: '',
  });

  async function load() {
    setLoading(true);
    try {
      const r = await fetch('/revrec/contracts');
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to load contracts');
      setItems(d.data?.items || d.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleAdd(e) {
    e.preventDefault();
    if (!form.customer_name || !form.contract_date || !form.total_contract_value) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch('/revrec/contracts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customer_name: form.customer_name,
          contract_date: form.contract_date,
          total_contract_value_cents: Math.round(parseFloat(form.total_contract_value) * 100),
          currency: form.currency.toUpperCase(),
          start_date: form.start_date || null,
          end_date: form.end_date || null,
          description: form.description,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message || 'Failed to create contract');
      setForm({ customer_name: '', contract_date: '', total_contract_value: '', currency: 'USD', start_date: '', end_date: '', description: '' });
      setShowForm(false);
      await load();
    } catch (e2) {
      setError(e2.message);
    } finally {
      setBusy(false);
    }
  }

  function fmtValue(cents, currency) {
    if (!cents) return '—';
    return `${currency} ${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
  }

  return (
    <section data-testid="revrec-contracts-page" data-loading={loading || busy ? 'true' : 'false'}>
      <header className="page-header">
        <div>
          <h1>Contracts</h1>
          <p className="muted">ASC 606 / IFRS 15 revenue recognition contracts with customers.</p>
        </div>
        <button className="btn primary" data-testid="revrec-contract-add" onClick={() => setShowForm((v) => !v)}>
          {showForm ? 'Cancel' : 'New Contract'}
        </button>
      </header>

      {error && <div className="alert error" data-testid="revrec-contracts-error">{error}</div>}

      {showForm && (
        <form onSubmit={handleAdd} className="card form-card row-form">
          <label>Customer name
            <input value={form.customer_name} onChange={(e) => setForm((f) => ({ ...f, customer_name: e.target.value }))} placeholder="e.g. Acme Corp" />
          </label>
          <label>Contract date
            <input type="date" value={form.contract_date} onChange={(e) => setForm((f) => ({ ...f, contract_date: e.target.value }))} />
          </label>
          <label>Total contract value
            <input type="number" step="0.01" value={form.total_contract_value} onChange={(e) => setForm((f) => ({ ...f, total_contract_value: e.target.value }))} placeholder="100000.00" />
          </label>
          <label>Currency
            <input value={form.currency} onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} placeholder="USD" maxLength={3} />
          </label>
          <label>Start date
            <input type="date" value={form.start_date} onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))} />
          </label>
          <label>End date
            <input type="date" value={form.end_date} onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))} />
          </label>
          <label>Description
            <input value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} placeholder="Contract notes" />
          </label>
          <div className="actions">
            <button className="btn primary" type="submit" disabled={busy || !form.customer_name || !form.contract_date || !form.total_contract_value}>Save</button>
          </div>
        </form>
      )}

      <div className="card">
        <table className="data-table" data-testid="revrec-contract-list">
          <thead>
            <tr><th>Customer</th><th>Contract date</th><th>Total value</th><th>Period</th><th>Status</th></tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} data-testid={`revrec-contract-row-${c.id}`}>
                <td>{c.customer_name}</td>
                <td>{c.contract_date}</td>
                <td>{fmtValue(c.total_contract_value_cents, c.currency)}</td>
                <td>{c.start_date && c.end_date ? `${c.start_date} → ${c.end_date}` : c.start_date || '—'}</td>
                <td><span className={`badge ${c.status === 'active' ? 'success' : ''}`}>{c.status || 'draft'}</span></td>
              </tr>
            ))}
            {!loading && items.length === 0 && (
              <tr><td colSpan={5} className="muted">No contracts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}
